import { useState, useEffect, useCallback } from 'react';
import { githubApi, type Installation, type PlanUsage, type Repo } from '@/services/githubApi';
import { docsApi } from '@/services/docsApi';

export interface SyncStatus {
  status: string;
  last_sync_at: string | null;
  file_count: number;
}

export function useGitHubConnection() {
  const [installations, setInstallations] = useState<Installation[]>([]);
  const [repos, setRepos] = useState<Repo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncStatuses, setSyncStatuses] = useState<Record<string, SyncStatus>>({});
  const [syncing, setSyncing] = useState<string | null>(null);
  const [planUsage, setPlanUsage] = useState<PlanUsage | null>(null);

  const refreshPlanUsage = useCallback(async () => {
    try {
      setPlanUsage(await githubApi.getPlanUsage());
    } catch {
      // Endpoint unavailable (older backend) — plan UI simply stays hidden.
      setPlanUsage(null);
    }
  }, []);

  const loadSyncStatuses = useCallback(async (connectedRepos: Repo[]) => {
    const statuses: Record<string, SyncStatus> = {};
    await Promise.all(
      connectedRepos.map(async (repo) => {
        try {
          const status = await docsApi.getSyncStatus(repo.owner, repo.repo);
          statuses[repo.full_name] = status;
        } catch {
          // ignore — status unknown
        }
      }),
    );
    setSyncStatuses(statuses);
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [installs, repoList] = await Promise.all([
        githubApi.listInstallations(),
        githubApi.listRepos(),
      ]);
      setInstallations(installs);
      setRepos(repoList);
      await loadSyncStatuses(repoList.filter((r) => r.connected));
      await refreshPlanUsage();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load GitHub data');
    } finally {
      setLoading(false);
    }
  }, [loadSyncStatuses, refreshPlanUsage]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const pollSyncStatus = useCallback(async (owner: string, repo: string) => {
    const fullName = `${owner}/${repo}`;
    const maxAttempts = 30; // ~60s at 2s intervals
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const status = await docsApi.getSyncStatus(owner, repo);
        setSyncStatuses((prev) => ({ ...prev, [fullName]: status }));
        if (status.status === 'completed' || status.status === 'failed') return;
      } catch {
        // ignore polling errors, keep trying
      }
    }
  }, []);

  const syncRepo = useCallback(async (owner: string, repo: string) => {
    const fullName = `${owner}/${repo}`;
    setSyncing(fullName);
    setSyncStatuses((prev) => ({
      ...prev,
      [fullName]: { status: 'syncing', last_sync_at: prev[fullName]?.last_sync_at ?? null, file_count: prev[fullName]?.file_count ?? 0 },
    }));
    try {
      await docsApi.syncRepo(owner, repo);
      // Sync is now async — poll for completion
      await pollSyncStatus(owner, repo);
    } catch {
      setSyncStatuses((prev) => ({
        ...prev,
        [fullName]: { ...prev[fullName], status: 'failed' },
      }));
    } finally {
      setSyncing(null);
    }
  }, [pollSyncStatus]);

  const connectRepo = useCallback(async (owner: string, repo: string) => {
    await githubApi.connectRepo(owner, repo);
    setRepos((prev) =>
      prev.map((r) =>
        r.full_name === `${owner}/${repo}` ? { ...r, connected: true } : r
      )
    );
    void refreshPlanUsage();
    // Auto-sync after connecting
    await syncRepo(owner, repo);
  }, [syncRepo, refreshPlanUsage]);

  const disconnectRepo = useCallback(async (owner: string, repo: string) => {
    await githubApi.disconnectRepo(owner, repo);
    setRepos((prev) =>
      prev.map((r) =>
        r.full_name === `${owner}/${repo}` ? { ...r, connected: false } : r
      )
    );
    setSyncStatuses((prev) => {
      const next = { ...prev };
      delete next[`${owner}/${repo}`];
      return next;
    });
    void refreshPlanUsage();
  }, [refreshPlanUsage]);

  const removeInstallation = useCallback(async (installationId: number) => {
    await githubApi.deleteInstallation(installationId);
    setInstallations((prev) => prev.filter((i) => i.installation_id !== installationId));
    await refresh();
  }, [refresh]);

  return {
    installations,
    repos,
    loading,
    error,
    syncStatuses,
    syncing,
    planUsage,
    refresh,
    connectRepo,
    disconnectRepo,
    syncRepo,
    removeInstallation,
  };
}
